import { useCallback, useEffect, useRef, useState } from "react";
import { useValidateBulkBookingMutation } from "@/redux/apis/clientBooking/clientBookingApi";
import { BulkBookingInputRow, BulkResolvedStopInput, BulkValidatedRow } from "@/redux/apis/clientBooking/types";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { BOOKING_FIELDS } from "./bookingFields";
import { ResolvedAddressMap, useBulkAddressResolution } from "./useBulkAddressResolution";

/**
 * Rows per request. Each one costs an address resolution and a price server-side, so a whole file in
 * one call would pass most server timeouts. Ten keeps a request to a few seconds and gives the table
 * something new to show that often.
 */
const CHUNK_SIZE = 10;

type UseBulkValidationArgs = {
  rows: BulkBookingInputRow[];
  accountId?: string;
  /** Skip until the step is actually open — validation is expensive and shouldn't run in the background. */
  enabled: boolean;
  /**
   * Identifies the SOURCE of the rows — the file, the header row, the column mapping. Deliberately
   * not the rows themselves: those change on every inline edit, and keying the full pass on them
   * meant fixing one cell re-resolved every address and re-priced every row. An edit now costs one
   * `revalidateRow` call instead of the whole file.
   */
  sourceKey: string;
  /**
   * The card every row is priced against. Changing it re-runs validation but deliberately does NOT
   * reset the row selection — see `sourceKey`, which owns that. Unticking three broken rows and then
   * switching card shouldn't bring them back.
   */
  paymentMethodUuid?: string;
};

/** The POI shape trimmed to what the booking payload needs. */
const toStopInput = (poi: PointsOfInterest): BulkResolvedStopInput => ({
  address: poi.address,
  lat: poi.lat,
  lng: poi.lng,
  city: poi.city,
  state: poi.state,
  country: poi.country,
});

/**
 * Might this failure be about the rows we sent, rather than the connection?
 *
 * If it might be, sending fewer rows can narrow down which one caused it. If it can't be, splitting
 * only multiplies a failure that was going to happen anyway.
 *
 * Written against RTK Query's error shape, where `status` is a number for an HTTP response and a
 * string tag otherwise.
 */
const isContentRejection = (error: unknown): boolean => {
  const shape = error as { status?: unknown; originalStatus?: unknown } | null;

  /*
   * A request the BROWSER could not complete counts, and this is the case that matters rather than a
   * defensive extra.
   *
   * The WAF answers a blocked request with a 403 carrying no `Access-Control-Allow-Origin`, so the
   * browser refuses to show it to JavaScript at all: `fetch` rejects, and RTK Query reports
   * `FETCH_ERROR` with no status. The 403 is only visible outside the browser, in curl, which is what
   * made this look like an HTTP problem when it is delivered as a network one.
   *
   * It genuinely overlaps with being offline — the two are indistinguishable here — which is what the
   * consecutive-failure guard in `run()` is for.
   */
  if (shape?.status === "FETCH_ERROR") return true;

  /*
   * `originalStatus` as well as `status`: a 4xx with a body that isn't JSON leaves
   * `status: "PARSING_ERROR"` and moves the real code here. Kept for a proxy or gateway that blocks
   * with CORS headers intact, where the status does reach us.
   */
  const status = typeof shape?.status === "number" ? shape.status : shape?.originalStatus;

  if (typeof status !== "number" || status < 400 || status >= 500) return false;

  /*
   * Two 4xx codes that say nothing about the rows, and where splitting makes things actively worse.
   *
   * 401/403 with an expired or rejected token fails identically for every sub-request, so splitting
   * would fire ~19 requests per chunk and then mark every row as unacceptable — telling someone their
   * spreadsheet is bad when they are simply logged out.
   *
   * 429 is worse still: the response is "you are making too many requests", and the reply to it would
   * be to make nineteen more.
   *
   * A WAF block is a 403 too, so that one is distinguished by its body: CloudFront answers text/html,
   * which fetchBaseQuery fails to parse, leaving `status: "PARSING_ERROR"` and the code in
   * `originalStatus`. Our own API answers 403 as a JSON ApiProblem, which parses, so `status` is the
   * number. That is the difference tested below.
   */
  const parsedJson = typeof shape?.status === "number";

  if (status === 429) return false;
  if ((status === 401 || status === 403) && parsedJson) return false;

  return true;
};

/**
 * A row the server would not accept at all, shaped like any other invalid row so the review table can
 * draw it.
 *
 * **Flagged on every column, because we genuinely do not know which one.** The request is turned away
 * before the application sees it, so nothing comes back naming a field. It was hung on `pickup` at
 * first, which read as an accusation: a row whose pickup was `=1+1` and whose DESTINATION held
 * `' OR 1=1 --` pointed the message squarely at the wrong cell. Tinting the lot says what is true —
 * somewhere in this row — instead of guessing.
 *
 * The wording offers unusual characters as the USUAL cause, not the diagnosis. It briefly named a
 * leading `=` and quote marks outright, which was inferred from a single file that happened to
 * contain both — the response says nothing about why, and a filter of this kind refuses content for
 * plenty of reasons. Stating a guess as a finding sends someone hunting for the wrong thing.
 */
const unacceptableRow = (row: BulkBookingInputRow): BulkValidatedRow => ({
  row_index: row.row_index,
  status: "invalid",
  errors: BOOKING_FIELDS.map((field) => ({
    field: field.key,
    level: "error" as const,
    message:
      "We couldn't check this booking — something in it was refused. Unusual characters are the usual cause. " +
      "Edit the row, or untick it to import the rest.",
  })),
  resolved: { pickup: null, destination: null, transfer_type_id: null, vehicle_id: null, pickup_datetime: null },
  estimated_total: null,
  is_pricing_available: true,
  payload: null,
});

/** Attach the browser-resolved stops to a row, so the server never resolves anything itself. */
const withResolvedStops = (row: BulkBookingInputRow, addresses: ResolvedAddressMap): BulkBookingInputRow => {
  const pickup = addresses.get((row.pickup ?? "").trim());
  const destination = addresses.get((row.destination ?? "").trim());

  return {
    ...row,
    resolvedPickup: pickup ? toStopInput(pickup) : null,
    resolvedDestination: destination ? toStopInput(destination) : null,
  };
};

export type BulkValidationState = {
  /** Validated rows in file order. Grows a chunk at a time. */
  validated: BulkValidatedRow[];
  /** How many rows have come back, for the progress line. */
  completed: number;
  total: number;
  isValidating: boolean;
  /** True while addresses are being looked up, before any row can be checked. */
  isResolving: boolean;
  /** Rows currently being re-checked after an edit — drawn as placeholders while in flight. */
  pendingRows: Set<number>;
  error?: string;
  /**
   * Re-check a single edited row. `preResolved` carries addresses the user picked from autocomplete —
   * already resolved, so they're used as-is rather than looked up again.
   */
  revalidateRow: (row: BulkBookingInputRow, preResolved?: Record<string, PointsOfInterest>) => Promise<void>;
  /** Replace a row's validated result — used after an inline edit. */
  retry: () => void;
};

/**
 * Validates the mapped rows a chunk at a time, so the review table fills in progressively instead of
 * hanging on the whole file.
 *
 * Chunks run **sequentially**, not in parallel: the server geocodes and prices each one, and firing
 * five at once would multiply the load for a table nobody can read that fast anyway.
 */
export const useBulkValidation = ({
  rows,
  accountId,
  enabled,
  sourceKey,
  paymentMethodUuid,
}: UseBulkValidationArgs): BulkValidationState => {
  const [validate] = useValidateBulkBookingMutation();
  const { resolveAddresses, isReady } = useBulkAddressResolution();
  const [validated, setValidated] = useState<BulkValidatedRow[]>([]);
  const [completed, setCompleted] = useState(0);
  const [isValidating, setIsValidating] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState<string>();
  const [attempt, setAttempt] = useState(0);
  const [pendingRows, setPendingRows] = useState<Set<number>>(new Set());
  /** Kept so re-validating an edited row can reuse the addresses already looked up. */
  const addressesRef = useRef<ResolvedAddressMap>(new Map());
  /** What has come back so far, readable inside the run without making it a dependency. */
  const validatedRef = useRef<BulkValidatedRow[]>([]);
  validatedRef.current = validated;
  /** Set by `retry` so the next run continues rather than starting over — see the effect. */
  const resumeRef = useRef(false);
  // Read through a ref so the effect sees the latest rows without depending on their identity.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // Guards against a chunk landing after the modal closed or the file changed, which would otherwise
  // append rows from an abandoned run.
  const runIdRef = useRef(0);

  useEffect(() => {
    // Google Maps has to be loaded before anything can be resolved.
    const rows = rowsRef.current;
    if (!enabled || !isReady || rows.length === 0) return;

    const runId = ++runIdRef.current;
    let cancelled = false;

    /**
     * Try again RESUMES; a new file starts over.
     *
     * The button only appears once the run has stopped, which now only happens on a connection
     * failure — so the rows already back came from successful responses and are not in doubt.
     * Re-asking about them costs a pricing call each, server-side, for answers we are holding.
     *
     * Read once and cleared, so the next run caused by anything else — a re-upload, a different
     * header row, a new column mapping — starts clean as it should.
     */
    const resuming = resumeRef.current;
    resumeRef.current = false;

    const run = async () => {
      setIsValidating(true);
      setIsResolving(true);
      setError(undefined);

      if (!resuming) {
        setValidated([]);
        setCompleted(0);
      }

      const done = resuming ? new Set(validatedRef.current.map((row) => row.row_index)) : new Set<number>();
      const outstanding = rows.filter((row) => !done.has(row.row_index));

      // Resolve every distinct address first, the same way the booking sheet resolves a typed one.
      // Deduplicated, so fifty airport transfers for one company is a couple of lookups.
      // Merged, not replaced: a retry after a failed chunk reuses whatever was already resolved
      // rather than paying for it twice.
      const wanted = outstanding.flatMap((row) => [row.pickup ?? "", row.destination ?? ""]);
      const unknown = wanted.map((address) => address.trim()).filter((address) => address && !addressesRef.current.has(address));
      const addresses = unknown.length > 0 ? await resolveAddresses(unknown) : new Map();
      if (cancelled || runIdRef.current !== runId) return;

      addressesRef.current = new Map([...addressesRef.current, ...addresses]);
      setIsResolving(false);

      const live = () => !cancelled && runIdRef.current === runId;

      /**
       * Single rows refused back to back, and whether anything has answered at all.
       *
       * A blocked request and a dropped connection are indistinguishable in the browser — both arrive
       * as `FETCH_ERROR` with no status — so splitting has to be attempted for either, and these two
       * tell them apart afterwards.
       *
       * The count alone was not enough. Three refusals in a row is decisive only while NOTHING has
       * succeeded, because that is what an outage looks like from the first request. Once a chunk has
       * answered, the connection is demonstrably fine, and three refusals in a row means three
       * unacceptable rows — which is ordinary in a file where someone pasted a column of quoted text.
       * Stopping there would have blamed the connection and thrown away the run.
       *
       * A generous ceiling still applies after that, for a connection that drops PART way through: it
       * allows a run of bad rows far longer than anything realistic, while stopping a hundred-row file
       * from becoming a thousand doomed requests.
       */
      let consecutiveRowFailures = 0;
      let anythingSucceeded = false;
      const GIVE_UP_BEFORE_ANY_SUCCESS = 3;
      const GIVE_UP_AFTER_SUCCESS = 10;

      /**
       * Check a chunk, halving it when the server REFUSES it, until the offending rows are alone.
       *
       * One row can poison a whole request without ever reaching the application. A row carrying
       * `' OR 1=1 --` in its destination — a real one, from a test file — is blocked by the WAF at the
       * edge, so the request comes back 403 from CloudFront as text/html. Nine perfectly good rows in
       * that chunk went down with it, and the loop then stopped, leaving every row after it unchecked.
       * Thirty bookings reported "20 of 20 have errors" and the rest simply weren't there.
       *
       * Halving isolates it in a handful of extra requests, so the file loses exactly the rows that
       * are genuinely unacceptable and the user can see which ones.
       *
       * A 5xx or a plainly-identified auth failure stops the run outright: every chunk would fail the
       * same way, so splitting would turn one outage into a request per row. Everything else is
       * attempted, and `consecutiveRowFailures` decides afterwards whether it was worth it.
       *
       * @returns false when the run should stop entirely.
       */
      const checkChunk = async (chunk: BulkBookingInputRow[]): Promise<boolean> => {
        if (!live()) return false;

        try {
          const response = await validate({ rows: chunk, accountId, paymentMethodUuid }).unwrap();
          if (!live()) return false;

          setValidated((current) => [...current, ...response.rows]);
          setCompleted((current) => current + chunk.length);
          // A chunk that answered proves the connection works, so nothing around it was an outage.
          consecutiveRowFailures = 0;
          anythingSucceeded = true;

          return true;
        } catch (rejection) {
          if (!live()) return false;

          if (!isContentRejection(rejection)) {
            setError("We couldn't check these bookings. Check your connection and try again.");

            return false;
          }

          if (chunk.length > 1) {
            const half = Math.ceil(chunk.length / 2);

            return (await checkChunk(chunk.slice(0, half))) && (await checkChunk(chunk.slice(half)));
          }

          consecutiveRowFailures += 1;

          if (consecutiveRowFailures >= (anythingSucceeded ? GIVE_UP_AFTER_SUCCESS : GIVE_UP_BEFORE_ANY_SUCCESS)) {
            setError("We couldn't check these bookings. Check your connection and try again.");

            return false;
          }

          // Down to one row and still refused: it is that row's own content. Reported against it so it
          // can be corrected or unticked, rather than costing the file everything after it.
          setValidated((current) => [...current, unacceptableRow(chunk[0])]);
          setCompleted((current) => current + 1);

          return true;
        }
      };

      for (let start = 0; start < outstanding.length; start += CHUNK_SIZE) {
        const chunk = outstanding.slice(start, start + CHUNK_SIZE).map((row) => withResolvedStops(row, addressesRef.current));
        if (!(await checkChunk(chunk))) break;
      }

      if (!cancelled && runIdRef.current === runId) {
        setIsValidating(false);
        setIsResolving(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // `sourceKey`, not `rows` — see the prop's comment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isReady, sourceKey, accountId, paymentMethodUuid, validate, resolveAddresses, attempt]);

  /** Re-check one row after an inline edit, replacing it in place. */
  const revalidateRow = useCallback(
    async (row: BulkBookingInputRow, preResolved?: Record<string, PointsOfInterest>) => {
      // An edit re-resolves an address and re-prices the row, which takes a moment — mark it so the
      // table shows something is happening rather than the old values sitting there unchanged.
      setPendingRows((current) => new Set(current).add(row.row_index));
      try {
        // Anything picked from autocomplete is already a resolved place — seed it rather than paying
        // Google again for an answer we have.
        Object.values(preResolved ?? {}).forEach((poi) => addressesRef.current.set(poi.address, poi));

        // Only look up what we don't already have. Without this filter every save re-resolved both
        // stops — six Google calls — even when the edit was to the passenger's name.
        const unknown = [row.pickup ?? "", row.destination ?? ""]
          .map((address) => address.trim())
          .filter((address) => address && !addressesRef.current.has(address));

        if (unknown.length > 0) {
          const addresses = await resolveAddresses(unknown);
          addressesRef.current = new Map([...addressesRef.current, ...addresses]);
        }

        const response = await validate({
          rows: [withResolvedStops(row, addressesRef.current)],
          accountId,
          paymentMethodUuid,
        }).unwrap();
        const updated = response.rows[0];
        if (!updated) return;

        setValidated((current) => current.map((existing) => (existing.row_index === updated.row_index ? updated : existing)));
      } catch {
        // Leave the row as it was — the user's edit stays visible and they can try again.
      } finally {
        setPendingRows((current) => {
          const next = new Set(current);
          next.delete(row.row_index);
          return next;
        });
      }
    },
    [validate, accountId, paymentMethodUuid, resolveAddresses]
  );

  const retry = useCallback(() => {
    resumeRef.current = true;
    setAttempt((current) => current + 1);
  }, []);

  // Waiting on the Maps script counts as busy: the effect below can't start until it's loaded, so
  // without this the review step sat empty and silent before anything appeared to happen.
  const isBusy = isValidating || (enabled && !isReady && rows.length > 0);

  return {
    validated,
    completed,
    total: rows.length,
    isValidating: isBusy,
    isResolving: isResolving || (enabled && !isReady),
    pendingRows,
    error,
    revalidateRow,
    retry,
  };
};
